import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ViewStyle } from 'react-native';
import { ICommunityResource } from '@/types/admin/resources';
import { ADMIN_COLORS } from '@/constants/admin';
import { ResourceIcon } from '@/components/admin/resources/common/ResourceIcon';
import { getResourceItemCount } from '@/components/admin/resources/common/resourceUtils';
import { SelectionIndicator } from '@/components/common/admin/mobile/SelectionIndicator';

interface ResourceCardProps {
  resource: ICommunityResource;
  onPress?: (resource: ICommunityResource) => void;
  onEdit?: (resource: ICommunityResource) => void;
  showEditButton?: boolean;
  style?: ViewStyle;
  isSelected?: boolean;
}

export const ResourceCard: React.FC<ResourceCardProps> = React.memo(({
  resource,
  onPress,
  onEdit,
  showEditButton = true,
  style,
  isSelected = false,
}) => {
  const totalItems = getResourceItemCount(resource);

  const handlePress = () => {
    if (onPress) {
      onPress(resource);
    }
  };

  const handleEditPress = () => {
    if (onEdit) {
      onEdit(resource);
    }
  };


  return (
    <TouchableOpacity
      style={[styles.container, isSelected && styles.selectedContainer, style]}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      {/* Icon and selection indicator */}
      <View style={styles.header}>
        <View style={styles.iconContainer}>
          <ResourceIcon icon={resource.icon} size={20} color={ADMIN_COLORS.TEXT.PRIMARY} />
        </View>
        {/* Selection indicator (matches funding datatable style) */}
        <View style={styles.dragHandle}>
          <SelectionIndicator selected={isSelected} size={16} />
        </View>
      </View>

      {/* Title and item count */}
      <View style={styles.titleContainer}>
        <Text style={styles.title} numberOfLines={1}>
          {resource.name}
        </Text>
        {totalItems > 0 && (
          <View style={styles.itemCountBadge}>
            <Text style={styles.itemCountText}>{totalItems}</Text>
          </View>
        )}
      </View>

      {/* Description */}
      <Text style={styles.description} numberOfLines={3}>
        {resource.description}
      </Text>

      {/* Edit button */}
      {showEditButton && (
        <TouchableOpacity
          style={styles.editButton}
          onPress={handleEditPress}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Text style={styles.editButtonText}>View/Edit</Text>
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
});

ResourceCard.displayName = 'ResourceCard';

const styles = StyleSheet.create({
  container: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    height: 160,
  },
  selectedContainer: {
    borderColor: ADMIN_COLORS.PRIMARY,
    borderWidth: 2,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dragHandle: {
    padding: 4, // Add some padding for better touch target
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    flex: 1,
  },
  itemCountBadge: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  itemCountText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontWeight: '500',
  },
  description: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    lineHeight: 16,
    flex: 1,
    marginBottom: 12,
  },
  editButton: {
    alignSelf: 'flex-start',
    marginTop: 'auto',
  },
  editButtonText: {
    fontSize: 12,
    color: ADMIN_COLORS.PRIMARY,
    fontWeight: '600',
  },
});
